import React, { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Autocomplete, Box, Divider, Stack, TextField, useTheme } from "@mui/material";

import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { Agg } from "system/query/Aggs";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import NetValueContent from "./NetValueContent";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { lightModeNamedColors } from "theme/infegyColor";
import { QueryFieldSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldSelector";
import { QueryField } from "system/query/QueryInfo";
import KeywordOptionSelector from "components/widgetParts/KeywordOptionSelector";
import WidgetPresetOptions from "components/widgetParts/WidgetPresetOptions";
import ColorSelector from "components/uiElements/inputs/colorSelector/ColorSelector";
import { AnyColor } from "react-colorful/dist/types";
import { socialDatasetId } from "system/query/Query";
import { QueryInfo } from "system/query/QueryInfo";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetQueryBehavior, WidgetRequest, WidgetTemplateQueryBehavior } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";

export type NetValueSettingsStateType = {
  abbreviate: boolean;
  alignment: string;
  color?: string;
  customWidgetTitle: string;
  showDecimals: boolean;
  widgetTitle: string;
  widgetTitleAlignment: string;
  viewAsPercentage: boolean;
  viewAsNetCount: boolean;
  viewAsPortion: boolean;
  displayType: string;
  colorMode: string;
  topColor: string;
  bottomColor: string;
  neutralColor: string;
  topValueLabel?: string;
  bottomValueLabel?: string;
}

export const defaultSettings: NetValueSettingsStateType = {
  abbreviate: false,
  alignment: 'center',
  color: undefined,
  customWidgetTitle: '',
  showDecimals: false,
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
  viewAsPercentage: true,
  viewAsNetCount: false,
  viewAsPortion: false,
  displayType: 'portion',
  colorMode: 'auto',
  topColor: lightModeNamedColors.positive,
  bottomColor: lightModeNamedColors.negative,
  neutralColor: lightModeNamedColors.mixed,
  topValueLabel: undefined,
  bottomValueLabel: undefined,
}

const defaultWidgetRequest = {
  name: 'net-value',
  endpoint: 'query/agg',
  queryParams: {
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
};

export function BuildDefaultNetValueRequests(queryInfo: QueryInfo): any {
  const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = { aggs: {} };

  if (queryInfo?.dataset?.id === socialDatasetId) {
    requestInitBody.aggs = { ["net_value"]: {
      field: "sentiment",
      op: "keyword",
      filter: ["p", "n"]
    }};
  } else {
    const foundField = queryInfo.fields?.find((field: QueryField) => {
      const numberOfOptions = parseInt(field?.options?.length + "");
      return ["keyword", "keywords"].includes(field.type) && numberOfOptions > 1;
    });
    if (foundField) {
      const firstOption = foundField?.options?.[0];
      const secondOption = foundField?.options?.[1];
      if (firstOption && firstOption.id && firstOption.label && secondOption && secondOption.id) {
        requestInitBody.aggs = { ["net_value"]: {
          field: foundField.id,
          op: "keyword",
          filter: [firstOption.id, secondOption.id]
        }};
      }
    }
  }

  if (Object.keys(requestInitBody.aggs).length) {
    widgetRequest.requestInit = { body: requestInitBody };
    return widgetRequest;
  }
}

export default function NetValueSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'net-value');
  const requestBody = originalRequest?.requestInit?.body || {};

  const keys = Object.keys(requestBody?.aggs || {});
  let firstAggKey = "";
  let aggDefinition = null;
  if (keys.length) {
    firstAggKey = keys[0];
    aggDefinition = requestBody.aggs[firstAggKey];
  }

  const updatedDefaultSettings = {...defaultSettings, color: theme.colors.primary.dark, ...props?.widget?.data?.settings};

  const [tabValue, setTabValue] = useState<number>(0);
  const [settingsState, setSettingsState] = useState<NetValueSettingsStateType>(updatedDefaultSettings);
  const [localAggs, setLocalAggs] = useState<Agg>(aggDefinition);

  // check if the data object has the fields needed
  const updateAggs = () => {
    const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = { aggs: {} };

    requestInitBody.aggs = localAggs ? { ["net_value"]: localAggs } : {};

    if (props.widget?.id && props.onUpdateWidgetData) {
      props.onUpdateWidgetData(props.widget?.id, {
        settings: settingsState,
      }).then(() => {
        if (props.widgetRefresh) {
          props.widgetRefresh();
        }
      })
    }

    if (Object.keys(requestInitBody.aggs).length) {
      widgetRequest.requestInit = { body: requestInitBody };
      props.saveWidgetRequests([widgetRequest]);
    } else {
      props.saveWidgetRequests([]);
    }
  }

  const displayTypeOptions = [
    { label: i18n.t("widget-types.net-value.settings.show-percentage"), value: "percentage" },
    { label: i18n.t("widget-types.net-value.settings.show-count"), value: "count" },
    { label: i18n.t("widget-types.net-value.settings.show-portion"), value: "portion" },
  ]

  const colorOptions = [
    { label: i18n.t("widget-types.net-value.settings.color-auto"), value: "auto" },
    { label: i18n.t("widget-types.net-value.settings.color-top"), value: "top" },
    { label: i18n.t("widget-types.net-value.settings.color-gradient"), value: "gradient" },
    { label: i18n.t("widget-types.net-value.settings.color-dominant"), value: "dominant" },
  ]

  const settingsOnChange = (updatedState: NetValueSettingsStateType) => setSettingsState(updatedState);

  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  const sentimentField = props.queryInfo?.fields?.find((field: QueryField) => field.id === "sentiment");
  const positiveLabel = sentimentField?.options?.find((option: any) => option.id === "p")?.label;
  const negativeLabel = sentimentField?.options?.find((option: any) => option.id === "n")?.label;

  const genderField = props.queryInfo?.fields?.find((field: QueryField) => field.id === "gender");
  const maleLabel = genderField?.options?.find((option: any) => option.id === "m")?.label;
  const femaleLabel = genderField?.options?.find((option: any) => option.id === "f")?.label;

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateAggs();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.net-value.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && <>
          {props.queryInfo && <>
            {/* Widget Preset Options - only available for social datasets */}
            {props.queryInfo?.dataset?.id === socialDatasetId &&
              <WidgetPresetOptions
                options={[{
                  label: `${i18n.t("sentiment")}: ${i18n.t("positivity")}`,
                  value: "positivity",
                },{
                  label: i18n.t("widget-types.net-value.settings.preset-gender"),
                  value: "gender",
                },{
                  label: i18n.t("widget-types.net-value.settings.preset-sentiment"),
                  value: "sentiment",
                }]}
                setSelectedOption={(newValue: string) => {
                  if (newValue === "sentiment") {
                    setLocalAggs({
                      field: "sentiment",
                      op: "keyword",
                      filter: ["p", "n"]
                    });
                    setSettingsState({
                      ...settingsState,
                      topColor: lightModeNamedColors.positive,
                      bottomColor: lightModeNamedColors.negative,
                      neutralColor: lightModeNamedColors.mixed,
                      viewAsNetCount: false,
                      viewAsPortion: false,
                      displayType: "percentage",
                      topValueLabel: positiveLabel,
                      bottomValueLabel: negativeLabel
                    });
                  } else if (newValue === "positivity") {
                    setLocalAggs({
                      field: "sentiment",
                      op: "keyword",
                      filter: ["p", "n"]
                    });
                    setSettingsState({
                      ...settingsState,
                      topColor: lightModeNamedColors.positive,
                      bottomColor: lightModeNamedColors.negative,
                      neutralColor: lightModeNamedColors.mixed,
                      viewAsPortion: true,
                      viewAsNetCount: false,
                      displayType: "portion",
                      topValueLabel: positiveLabel,
                      bottomValueLabel: negativeLabel
                    });
                  } else if (newValue === "gender") {
                    setLocalAggs({
                      field: "gender",
                      op: "keyword",
                      filter: ["f", "m"]
                    });
                    setSettingsState({
                      ...settingsState,
                      topColor: lightModeNamedColors.female,
                      bottomColor: lightModeNamedColors.male,
                      neutralColor: lightModeNamedColors.neutral,
                      viewAsNetCount: false,
                      viewAsPortion: true,
                      displayType: "portion",
                      topValueLabel: maleLabel,
                      bottomValueLabel: femaleLabel
                    });
                  }
                }}>
              </WidgetPresetOptions>
            }
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box>
                <SimpleFormLabel>{i18n.t("widget-types.net-value.settings.display-type")}</SimpleFormLabel>
                <Autocomplete
                  disableClearable
                  options={displayTypeOptions}
                  getOptionLabel={(option: OptionsValueType) => option.label}
                  value={
                    displayTypeOptions.find((option: OptionsValueType) => (
                      option.value === settingsState.displayType
                    )) || displayTypeOptions[0]
                  }
                  renderInput={(params) => <TextField autoComplete="off" {...params} />}
                  onChange={(e, newValue: OptionsValueType) => {
                    setSettingsState({
                      ...settingsState,
                      displayType: newValue.value + "",
                    });
                  }}
                />
              </Box>
              <Box>
                <SimpleFormLabel>{i18n.t("widget-types.net-value.settings.color-mode")}</SimpleFormLabel>
                <Autocomplete
                  disableClearable
                  options={colorOptions}
                  getOptionLabel={(option: OptionsValueType) => option.label}
                  value={
                    colorOptions.find((option: OptionsValueType) => (
                      option.value === settingsState.colorMode
                    )) || colorOptions[0]
                  }
                  renderInput={(params) => <TextField autoComplete="off" {...params} />}
                  onChange={(e, newValue: OptionsValueType) => {
                    setSettingsState({
                      ...settingsState,
                      colorMode: newValue.value + "",
                    });
                  }}
                />
              </Box>
            </Box>
            <SimpleFormLabel>{i18n.t("field")}</SimpleFormLabel>
            <QueryFieldSelector
              requireAggSupported={true}
              allowedFieldTypes={["keyword", "keywords"]}
              fields={props.queryInfo?.fields}
              selectedFieldId={localAggs?.field || ""}
              setSelectedFieldId={(newFieldId:string) => {
                setLocalAggs({
                  field: newFieldId,
                  op: "keyword",
                  filter: [],
                  labels: [],
                });
                setSettingsState({
                  ...settingsState,
                  topValueLabel: undefined,
                  bottomValueLabel: undefined,
                });
              }}
            />

            {/* Top Value */}
            <SimpleFormLabel>{i18n.t("widget-types.net-value.top-value")}</SimpleFormLabel>
            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 2 }}>
              <ColorSelector
                color={settingsState.topColor ?? ''}
                showInput={false}
                onChange={(color: AnyColor) => {
                  setSettingsState({
                    ...settingsState,
                    topColor: color.toString(),
                  });
                }} />
              <KeywordOptionSelector
                key={`top-value-selector-${localAggs?.field || ""}`}
                queryInfo={props.queryInfo}
                selectedField={props.queryInfo?.fields?.find((field: QueryField) => field.id === localAggs?.field)}
                enforceFixedSelection={true}
                selectedKeywordOption={localAggs?.filter?.[0]}
                selectedKeywordLabel={localAggs?.labels?.[0]}
                setSelectedKeywordOption={(newKeywordOption: string | undefined, newKeywordOptionLabel?: string | undefined) => {
                  if (!newKeywordOption) {
                    newKeywordOption = "";
                  }
                  const filter = localAggs?.filter ? [...localAggs.filter] : [];
                  filter[0] = newKeywordOption;
                  const labels = localAggs?.labels ? [...localAggs.labels] : [];
                  labels[0] = newKeywordOptionLabel || "";
                  setLocalAggs({
                    ...localAggs,
                    filter,
                    labels,
                  });
                  setSettingsState({
                    ...settingsState,
                    topValueLabel: newKeywordOptionLabel,
                  });
                }} />
            </Box>

            {/* Neutral Value */}
            {((settingsState?.displayType === "portion" && settingsState?.colorMode === "auto") || settingsState?.colorMode === "gradient") &&
              <>
                <SimpleFormLabel>{i18n.t("widget-types.net-value.settings.neutral-color")}</SimpleFormLabel>
                <Box sx={{ display: "grid", gridTemplateColumns: "auto auto 1fr", gap: 2 }}>
                  <ColorSelector
                    color={settingsState.neutralColor ?? ''}
                    showInput={false}
                    onChange={(color: AnyColor) => {
                      setSettingsState({
                        ...settingsState,
                        neutralColor: color.toString(),
                      });
                    }} />
                  <SimpleFormLabel>{i18n.t("widget-types.net-value.settings.neutral-color")}</SimpleFormLabel>
                  <Box sx={{
                    background: `linear-gradient(to right, ${settingsState.topColor}, ${settingsState.neutralColor}, ${settingsState.bottomColor})`,
                    height: "100%",
                    borderRadius: "6px",
                    flex: 1
                  }} />
                </Box>
              </>
            }

            {/* Bottom Value */}
            <SimpleFormLabel>{i18n.t("widget-types.net-value.bottom-value")}</SimpleFormLabel>
            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 2 }}>
              <ColorSelector
                color={settingsState.bottomColor ?? ''}
                showInput={false}
                onChange={(color: AnyColor) => {
                  setSettingsState({
                    ...settingsState,
                    bottomColor: color.toString(),
                  });
                }} />
              <KeywordOptionSelector
                key={`bottom-value-selector-${localAggs?.field || ""}`}
                queryInfo={props.queryInfo}
                selectedField={props.queryInfo?.fields?.find((field: QueryField) => field.id === localAggs?.field)}
                enforceFixedSelection={true}
                excludeOptions={[localAggs?.filter?.[0] ?? ""]}
                selectedKeywordOption={localAggs?.filter?.[1]}
                selectedKeywordLabel={localAggs?.labels?.[1]}
                setSelectedKeywordOption={(newKeywordOption: string | undefined, newKeywordOptionLabel?: string | undefined) => {
                  if (!newKeywordOption) {
                    newKeywordOption = "";
                  }
                  const filter = localAggs?.filter ? [...localAggs.filter] : [];
                  filter[1] = newKeywordOption;
                  const labels = localAggs?.labels ? [...localAggs.labels] : [];
                  labels[1] = newKeywordOptionLabel || "";
                  setLocalAggs({
                    ...localAggs,
                    filter,
                    labels,
                  });
                  setSettingsState({
                    ...settingsState,
                    bottomValueLabel: newKeywordOptionLabel,
                  });
                }} />
            </Box>
          </>}
        </>}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Preview */}
            <Box sx={{ position:"relative", height: "200px"}}>
              <NetValueContent
                fieldLabel={i18n.t("field")}
                topValue={100}
                bottomValue={50}
                topLabel={i18n.t("top")}
                bottomLabel={i18n.t("bottom")}
                settingsState={settingsState}
                widgetQuery={props.widgetQuery}
                />
            </Box>

            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                  {settingsState.widgetTitle === 'custom' &&
                    <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                      <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                      <SettingsTextField
                        keyid="customWidgetTitle"
                        label={i18n.t("widget-settings.custom-text")}
                        value={settingsState.customWidgetTitle}
                      />
                    </Stack>}
                {/* Widget Title Alignment */}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
              {/* Widget Text Alignment */}
              <SettingsToggleButton
                keyid="alignment"
                label={i18n.t("widget-settings.alignment")}
                optionsList={horizontalAlignmentOptions}
                value={settingsState.alignment} />
            </SettingsOptions>
          </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  )
}
